import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { useConsent } from "@/contexts/consent-context";
import { useAuth } from "@/hooks/use-auth";
import { CookiePreferencesModal } from "./cookie-preferences-modal";
import { Link, useLocation } from "wouter";

/** Only show on public-facing pages — never in admin or user dashboard */
function useIsPublicPage() {
  const [location] = useLocation();
  return !location.startsWith("/admin") && !location.startsWith("/dashboard");
}

/**
 * Cookie consent banner — only shown to unauthenticated users on public pages
 * who have not yet responded to the consent prompt.
 * Authenticated users are not subject to the consent flow (out of scope).
 */
export function CookieBanner() {
  const { consent, acceptAll, rejectNonEssential } = useConsent();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const isPublic = useIsPublicPage();

  useEffect(() => {
    // Only show for unauthenticated users on public pages who haven't responded
    if (!authLoading && !isAuthenticated && !consent.hasResponded && isPublic) {
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [authLoading, isAuthenticated, consent.hasResponded, isPublic]);

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur shadow-lg"
        data-testid="cookie-banner"
        role="region"
        aria-label="Cookie consent"
      >
        <div className="mx-auto max-w-7xl px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              We use cookies to improve your experience. Strictly necessary cookies are always active.{" "}
              <Link href="/page/cookie-policy" className="text-primary underline hover:no-underline">
                Cookie Policy
              </Link>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPrefs(true)}
              data-testid="button-manage-cookies"
            >
              Manage
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={rejectNonEssential}
              data-testid="button-reject-cookies"
            >
              Reject non-essential
            </Button>
            <Button
              size="sm"
              onClick={acceptAll}
              data-testid="button-accept-all-cookies"
            >
              Accept all
            </Button>
          </div>
        </div>
      </div>

      {showPrefs && <CookiePreferencesModal onClose={() => setShowPrefs(false)} />}
    </>
  );
}
